import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/lib/api";
import { Loader2, AlertCircle, Printer, ShieldCheck } from "lucide-react";
import { SharePreview, RANGES } from "@/pages/CareSummary";

/**
 * /s/:token — public, read-only view of a Care Summary the caregiver built
 * and shared. Renders the frozen snapshot with the SAME SharePreview the
 * caregiver saw in the builder, so what they previewed is exactly what the
 * recipient sees (patient profile included). No auth; token is the capability.
 */
export default function CareShareView() {
    const { token } = useParams();
    const [state, setState] = useState({ loading: true, error: null, data: null });

    useEffect(() => {
        fetch(`${API}/care-share/${token}`)
            .then(async (r) => {
                if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "This link is not available.");
                return r.json();
            })
            .then((d) => setState({ loading: false, error: null, data: d }))
            .catch((e) => setState({ loading: false, error: e.message, data: null }));
    }, [token]);

    if (state.loading) {
        return <div className="min-h-screen bg-cream flex items-center justify-center text-[#6E7A74]"><Loader2 className="animate-spin mr-2" /> Loading care summary…</div>;
    }
    if (state.error) {
        return (
            <div className="min-h-screen bg-cream flex items-center justify-center px-4">
                <div className="ml-card p-8 max-w-md text-center" data-testid="care-share-error">
                    <AlertCircle className="mx-auto text-coral-dark mb-3" size={28} />
                    <p className="text-[#1F2624] font-medium">{state.error}</p>
                    <p className="text-sm text-[#6E7A74] mt-2">Ask the caregiver to send a fresh link.</p>
                </div>
            </div>
        );
    }

    const d = state.data;
    const first = (d.loved_one_name || "").split(" ")[0];
    const sections = new Set(d.sections || []);
    const docSel = new Set((((d.summary || {}).journey || {}).documents || []).map(x => x.id));
    const rangeLabel = (RANGES.find(r => r.key === (d.range || "all")) || {}).label || "Everything";
    return (
        <div className="min-h-screen bg-cream py-8 px-4" data-clarity-mask="true">
            <div className="max-w-3xl mx-auto" data-testid="care-share-view">
                <div className="flex items-center justify-between mb-4 print:hidden">
                    <div className="flex items-center gap-2 text-sm text-teal-dark">
                        <ShieldCheck size={16} /> Shared securely via Memory Lane
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => window.open(`${API}/care-share/${token}/pdf`, "_blank")} className="ml-btn-secondary !min-h-[38px] !px-4 text-[13px]" data-testid="care-share-pdf"><Printer size={15} /> PDF</button>
                    </div>
                </div>
                <h1 className="font-heading text-2xl font-semibold text-[#12776F]">{d.title}{first ? ` — ${first}` : ""}</h1>
                <p className="text-sm text-[#6E7A74] mt-1">Shared by {d.caregiver_name || "a caregiver"}. This contains only what they chose to include.</p>
                <p className="text-xs text-[#6E7A74] mt-1 mb-6" data-testid="care-share-window">Includes: {rangeLabel} · Created {String(d.created_at || "").slice(0, 10)}</p>
                <SharePreview data={d.summary} sections={sections} docSel={docSel} range={d.range || "all"} shareNotes={sections.has("notes")} />
                <footer className="mt-8 pt-4 border-t border-cream-dark text-xs text-[#6E7A74]">
                    Memory Lane is a caregiver-support tool, not a medical record.
                </footer>
            </div>
        </div>
    );
}
